//-- Copyright (c) 2021 TMT Insights Group, LLC
import * as Yup from 'yup';
import { cloneDeep } from 'lodash';
import { Store } from '@reduxjs/toolkit';
import { NumberLocale, StringLocale } from 'yup/lib/locale';

let store: Store;
export const injectStore = (_store: Store) => {
  store = _store;
};

Yup.setLocale({
  mixed: {
    required: 'Required'
  },
  string: {
    min: 'Must be at least ${min} characters.',
    max: 'Must be no longer than ${max} characters.',
    required: 'Required'
  } as StringLocale & { required: string },
  number: {
    min: 'Must be a number above ${min}.',
    max: 'Must be a number below ${max}.',
    required: 'Required'
  } as NumberLocale & { required: string }
});

export const validateProfile = (fields: any) => {
  const requiredFields: any = { name: 'Profile Name', customer_name: 'Customer' };
  const requiredDataFields: any = { workflow: 'Workflow' };
  const toastErrors: any[] = [],
    errors: any = {};
  const email_notification = fields.data?.['email_notification'];

  Object.keys(requiredFields).forEach((field) => {
    if (!fields[field]?.length) {
      toastErrors.push(`Field '${requiredFields[field]}' is required`);
      errors[field] = true;
    }
  });

  Object.keys(requiredDataFields).forEach((field) => {
    if (!fields.data?.[field]?.length) {
      toastErrors.push(`Field '${requiredDataFields[field]}' is required`);
      errors[field] = true;
    }
  });

  if (email_notification && !email_notification?.template) {
    delete email_notification.to;
    delete email_notification.cc;
    delete email_notification.bcc;
  }

  if (email_notification && email_notification?.template && !email_notification.to) {
    toastErrors.push('To send a delivery notification, it needs at least one To: email address.');
    errors['email_notification.to'] = true;
  }

  return { errors, toastErrors };
};

export const parseWorkflowSchema = (schema: any = {}) => {
  const { instance_schema, template_schema } = schema;
  schema.options = {};

  const parseSchemaProperties = (properties: any) => {
    if (properties.enum) {
      const labels = properties.enum_names || properties.enum;
      properties.options = properties.enum.map((value: string, index: number) => ({ value, label: labels[index] }));
      schema.options[properties.key] = properties.enum.map((value: string, index: number) => ({
        value,
        label: labels[index]
      }));
    }
    if (properties.properties) {
      properties.properties.forEach((props: any) => parseSchemaProperties(props));
    }
    return properties;
  };

  if (instance_schema) {
    // console.log('do something with this', instance_schema)
  }

  if (template_schema) {
    if (template_schema.properties) {
      template_schema.properties.forEach((prop: any) => parseSchemaProperties(prop));
    }

    if (template_schema.fieldsets) {
      template_schema.fieldsets.forEach((fs: any) => {
        fs.properties?.forEach((fsProps: any) => {
          parseSchemaProperties(fsProps);
          fsProps.color = fs.fieldset_type || 'default';
        });
      });
    }
  }
  return schema;
};

const getDefaultValueForType = (type: string) => {
  switch (type) {
    case 'string':
    case 'int':
    case 'number':
      return '';
    case 'bool':
      return false;
    case 'object':
      return {};
    case 'array':
      return [];
    default:
      return null;
  }
};

const INPUT_TYPES = ['string', 'number', 'bool', 'array', 'object', 'version_selector', 'select'];

export const getWorkflowValidation = (schema: any) => {
  if (!schema) return null;
  const validation: any = { workflow: Yup.string().required() };
  const setSchema = (props = []) => {
    props?.forEach((prop: any) => {
      const allowedType = INPUT_TYPES.includes(prop.type);
      if (!allowedType) {
        console.log(`%c${prop.type} is not a valid type. Choose one of: ${INPUT_TYPES.join(', ')}`, 'color: cyan');
      }
      const type = allowedType ? prop.type : 'string';
      validation[prop.key] = (Yup as any)
        [type]()
        .concat(prop.required ? (Yup as any)[type]().required().min(1) : (Yup as any)[type]())
        .concat(prop.min_value ? (Yup as any)[type]().min(prop.min_value) : (Yup as any)[type]())
        .concat(prop.max_value ? (Yup as any)[type]().max(prop.max_value) : (Yup as any)[type]());
    });
  };
  if (schema.fieldsets) {
    schema.fieldsets.map((fs: any) => fs.properties && setSchema(fs.properties));
  }
  if (schema.properties) {
    setSchema(schema.properties);
  }
  return validation;
};

export const initializeProfileData = (workflowName: any) => {
  const profileState = store?.getState()?.profile || {};
  const profileData = profileState.data?.data;
  const options = profileState.workflows?.[workflowName]?.options;

  if (!profileData || !options) return {};

  // if the saved value in our profile does not exist in workflow options
  // clear it from the profile
  const profile = cloneDeep(profileData);
  profile.workflow = workflowName;
  Object.keys(options).forEach((key) => {
    if (profile[key]) {
      const possibleValues = options[key].map((option: any) => option.value);
      if (!possibleValues.includes(profile[key])) {
        profile[key] = '';
      }
    }
  });

  return profile;
};

export const initializeSchema = (schema: any) => {
  const titleValidation: any = {};
  Object.keys(schema).forEach((key) => {
    const allowedType = INPUT_TYPES.includes(schema[key].type);
    if (!allowedType) return;
    let type = schema[key].type;
    if (schema[key].type === 'version_selector' || schema[key].type === 'select') type = 'string';
    const required = schema[key].required;
    titleValidation[key] = (Yup as any)[type]().concat(required ? (Yup as any)[type]().required() : (Yup as any)[type]());
  });
  return titleValidation;
};

export const getSchemaInitialValues = (schema: any) => {
  if (!Object.keys(schema)?.length) return null;
  const defaults: any = {};
  Object.keys(schema).forEach((key) => {
    const type = schema[key].type;
    defaults[key] = schema[key]?.default_value || getDefaultValueForType(type);
  });
  return defaults;
};
